import { Navigate, useParams } from 'react-router-dom';
import { useProjects } from '../hooks/useProjects';
import { usePageTitle } from '../hooks/usePageTitle';
import { useStaticSession } from '../hooks/useStaticSession';

/**
 * Renders a static sub-project page inside the portal shell. Sidebar (file list) lives in MainLayout;
 * this component just embeds the selected page as an `<iframe>` so the file's own CSS / inline JS work
 * unchanged. Default path is `index.html` when the URL has no extra segments.
 *
 * Redirects to `/` if the slug isn't a known static project — guards against typos in the URL.
 */
export default function StaticProjectPage() {
  const params = useParams();
  const slug = params.slug;
  const splat = params['*'] ?? '';
  const currentPath = splat.length > 0 ? splat : 'index.html';
  usePageTitle(slug ?? 'static');

  const { data: projects, isLoading } = useProjects();
  if (isLoading) return <p className="muted">Loading...</p>;
  if (!slug) return <Navigate to="/" replace />;
  const project = projects?.find((p) => p.slug === slug);
  if (!project || project.type !== 'static') return <Navigate to="/" replace />;

  return <StaticFrame title={project.name} src={`/api/v1/${encodeURIComponent(slug)}/${encodePath(currentPath)}`} />;
}

/**
 * The iframe itself. Static routes require auth, and an iframe can't send our headers, so the frame
 * only mounts once the server has set the `ailab_session` cookie the browser will send instead.
 */
function StaticFrame({ title, src }: { title: string; src: string }) {
  const { data, error } = useStaticSession();
  // A failed background re-mint keeps the frame: the cookie from the last success is still live.
  if (data) return <iframe title={title} src={src} className="static-iframe" data-testid="static-iframe" />;
  if (error) {
    return (
      <p className="muted" data-testid="static-session-error">
        Could not open this page: {error.message}
      </p>
    );
  }
  return <p className="muted">Loading...</p>;
}

/** Per-segment URL-encode a slash-separated path. Preserves separators; encodes spaces and other chars. */
function encodePath(path: string): string {
  return path.split('/').map(encodeURIComponent).join('/');
}
